import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { apiPost } from '@/lib/api';

/** Phase 08 — homework help that teaches the method and works a DIFFERENT
 * example, then hands the pen back. Never the answer they were set. */
export function HomeworkHelpDialog({ open, onOpenChange, subject = '', chapter = '' }) {
  const [question, setQuestion] = useState('');
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);

  const help = async () => {
    if (!question.trim()) return;
    setBusy(true);
    try { setOut(await apiPost('/premium/v1/homework/help', { question, subject, chapter })); }
    catch (e) { toast.error(e?.message || 'Homework help is unavailable right now'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setOut(null); setQuestion(''); } onOpenChange(v); }}>
      <DialogContent className="max-w-xl" data-testid="homework-help-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Stuck on homework?</DialogTitle>
          <DialogDescription className="font-body text-xs">
            Paste the question. I'll show you the method and work a similar one — you'll do yours.
          </DialogDescription>
        </DialogHeader>

        <Textarea value={question} onChange={e => setQuestion(e.target.value)} rows={3}
          placeholder="Q. Find the area of a triangle whose base is 12 cm and height 7 cm"
          className="rounded-xl text-sm" data-testid="homework-input" />
        <Button onClick={help} disabled={busy || !question.trim()} className="rounded-xl" data-testid="homework-submit-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Show me how
        </Button>

        {out && (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1" data-testid="homework-answer">
            <p className="font-body text-sm text-slate-700 whitespace-pre-wrap">{out.method}</p>
            <p className="font-body text-[11px] text-slate-500 flex items-start gap-1.5 border-t border-slate-100 pt-2">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" /> {out.disclaimer}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default HomeworkHelpDialog;
